import React from 'react';
import { FonctionArmement, Competence, CompetenceFonctionArmement } from '../../../../../../shared/types/config';

interface FonctionsArmementTabProps {
  fonctionsArmement: FonctionArmement[];
  searchFonctionsArmement: string;
  onSearchChange: (value: string) => void;
  onAdd: () => void;
  onEdit: (fonctionArmement: FonctionArmement) => void;
  onDelete: (id: number) => void;
  competences: Competence[];
}

const FonctionsArmementTab: React.FC<FonctionsArmementTabProps> = ({
  fonctionsArmement,
  searchFonctionsArmement,
  onSearchChange,
  onAdd,
  onEdit,
  onDelete,
  competences,
}) => {
  const filteredFonctionsArmement = fonctionsArmement.filter(f => {
    return f.nom.toLowerCase().includes(searchFonctionsArmement.toLowerCase()) ||
      (f.description || '').toLowerCase().includes(searchFonctionsArmement.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Rechercher une fonction d'armement..."
            value={searchFonctionsArmement}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60"
          />
          <svg className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-cockpit-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <button
          onClick={onAdd}
          className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-semibold text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors flex items-center gap-2 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b border-[#1a2537]">
            <tr>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">ID</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Nom</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Description</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Compétences</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFonctionsArmement.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-cockpit-text-2">
                  Aucune fonction d'armement trouvée
                </td>
              </tr>
            ) : (
              filteredFonctionsArmement.map((fonctionArmement) => {
                let competencesData: Array<{id: number, nom: string, ordre: number, optionnel: boolean, ref: boolean}> = [];
                if (fonctionArmement.id_competences && Array.isArray(fonctionArmement.id_competences)) {
                  if (fonctionArmement.id_competences.length > 0) {
                    const firstItem = fonctionArmement.id_competences[0];
                    if (typeof firstItem === 'number') {
                      competencesData = (fonctionArmement.id_competences as unknown as number[])
                        .map((id, index) => ({
                          id,
                          nom: competences.find(c => c.id === id)?.nom || `ID: ${id}`,
                          ordre: index + 1,
                          optionnel: false,
                          ref: false
                        }))
                        .filter(c => c.nom);
                    } else if (typeof firstItem === 'object' && firstItem !== null && 'id' in firstItem && 'ordre' in firstItem) {
                      competencesData = (fonctionArmement.id_competences as CompetenceFonctionArmement[])
                        .map(comp => ({
                          id: comp.id,
                          nom: competences.find(c => c.id === comp.id)?.nom || `ID: ${comp.id}`,
                          ordre: comp.ordre,
                          optionnel: comp.optionnel || false,
                          ref: comp.ref || false
                        }))
                        .filter(c => c.nom)
                        .sort((a, b) => a.ordre - b.ordre);
                    }
                  }
                }
                return (
                  <tr key={fonctionArmement.id} className="border-b border-[#1a2537]/60 hover:bg-[#0b1220]/40 transition-colors">
                    <td className="px-4 py-3 text-cockpit-text-2">{fonctionArmement.id}</td>
                    <td className="px-4 py-3 text-cockpit-text font-medium">{fonctionArmement.nom}</td>
                    <td className="px-4 py-3 text-cockpit-text-2 max-w-xs truncate">
                      {fonctionArmement.description || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {competencesData.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {competencesData.map((comp, index) => {
                            const statusLabels = [];
                            if (comp.optionnel) statusLabels.push('optionnel');
                            if (comp.ref) statusLabels.push('ref');
                            const statusText = statusLabels.length > 0 ? ` (${statusLabels.join(', ')})` : '';
                            return (
                              <span
                                key={index}
                                className={`px-2 py-1 rounded text-xs border ${
                                  comp.ref
                                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                                    : comp.optionnel
                                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                                    : 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                                }`}
                                title={`Ordre: ${comp.ordre}${statusText}`}
                              >
                                {comp.ordre}. {comp.nom}{statusText}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-cockpit-muted text-xs">Aucune</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onEdit(fonctionArmement)}
                          className="rounded border border-[#6366f1]/40 bg-[#6366f1]/20 p-1.5 text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors"
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => fonctionArmement.id && onDelete(fonctionArmement.id)}
                          className="rounded border border-red-500/35 bg-red-500/10 p-1.5 text-red-300 hover:bg-red-500/20 transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FonctionsArmementTab;
